import React, { useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  XCircle,
  Info,
  Sparkle,
  X,
} from "@phosphor-icons/react";
import { create } from "zustand";
import { cn } from "../../lib/cn";
import { useUIStore } from "../../stores/ui";

type ToastType = "success" | "error" | "info" | "ai";

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastState {
  toasts: ToastItem[];
  add: (toast: Omit<ToastItem, "id">) => string;
  remove: (id: string) => void;
  clear: () => void;
}

let counter = 0;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  add: (toast) => {
    const id = `toast-${++counter}-${Date.now()}`;
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    return id;
  },
  remove: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
  clear: () => set({ toasts: [] }),
}));

function useToast() {
  const add = useToastStore((s) => s.add);
  const remove = useToastStore((s) => s.remove);
  const clear = useToastStore((s) => s.clear);

  const toast = useCallback((opts: Omit<ToastItem, "id">) => add(opts), [add]);
  const success = useCallback(
    (title: string, description?: string) => add({ type: "success", title, description }),
    [add],
  );
  const error = useCallback(
    (title: string, description?: string) => add({ type: "error", title, description }),
    [add],
  );
  const info = useCallback(
    (title: string, description?: string) => add({ type: "info", title, description }),
    [add],
  );
  const ai = useCallback(
    (title: string, description?: string) => add({ type: "ai", title, description }),
    [add],
  );

  return useMemo(
    () => ({ toast, success, error, info, ai, dismiss: remove, clear }),
    [toast, success, error, info, ai, remove, clear],
  );
}

const typeConfig: Record<
  ToastType,
  { icon: React.ReactNode; container: string; iconColor: string }
> = {
  success: {
    icon: <CheckCircle weight="fill" size={18} />,
    container: "border-[var(--color-signal-green)]/20",
    iconColor: "text-[var(--color-signal-green)]",
  },
  error: {
    icon: <XCircle weight="fill" size={18} />,
    container: "border-[var(--color-soft-red)]/20",
    iconColor: "text-[var(--color-soft-red)]",
  },
  info: {
    icon: <Info weight="fill" size={18} />,
    container: "border-[var(--color-cool-azure)]/20",
    iconColor: "text-[var(--color-cool-azure)]",
  },
  ai: {
    icon: <Sparkle weight="fill" size={18} />,
    container: "border-[var(--color-lever-deep)]/20 shadow-[var(--glow-ai)]",
    iconColor: "text-[var(--color-lever-deep)]",
  },
};

const DEFAULT_DURATION = 4000;
const MINIMAL_DURATION = 2000;

function ToastEntry({ item }: { item: ToastItem }) {
  const remove = useToastStore((s) => s.remove);
  const toastStyle = useUIStore((s) => s.toastStyle);
  const config = typeConfig[item.type];
  const minimal = toastStyle === "minimal";

  useEffect(() => {
    const base = minimal ? MINIMAL_DURATION : DEFAULT_DURATION;
    const ms = item.duration ?? base;
    if (ms <= 0) return;
    const timer = setTimeout(() => remove(item.id), ms);
    return () => clearTimeout(timer);
  }, [item.id, item.duration, remove, minimal]);

  if (minimal) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: -8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.97 }}
        transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
        role="alert"
        aria-live="polite"
        className={cn(
          "pointer-events-auto relative flex items-center gap-2",
          "rounded-[var(--radius-lg)] px-3 py-1.5",
          "bg-[var(--bg-surface)]/90 backdrop-blur-[var(--blur-md)]",
          "border border-[var(--border-hairline)]",
          "shadow-[var(--shadow-sm)]",
        )}
      >
        <span className={cn("shrink-0", config.iconColor)}>
          {React.cloneElement(config.icon as React.ReactElement<{ size: number }>, { size: 13 })}
        </span>
        <p className="text-[11px] font-medium text-[var(--text-secondary)] leading-snug truncate max-w-[200px]">
          {item.title}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
      role="alert"
      aria-live="assertive"
      className={cn(
        "pointer-events-auto relative flex items-start gap-3",
        "w-80 rounded-[var(--radius-xl)] p-4",
        "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-md)]",
        "border",
        "shadow-[var(--shadow-md)]",
        config.container,
      )}
    >
      <span className={cn("mt-0.5 shrink-0", config.iconColor)}>
        {config.icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] leading-snug break-words">
          {item.title}
        </p>
        {item.description && (
          <p className="mt-1 text-xs text-[var(--text-secondary)] leading-relaxed break-words">
            {item.description}
          </p>
        )}
      </div>
      <button
        onClick={() => remove(item.id)}
        aria-label="Dismiss notification"
        className={cn(
          "shrink-0 inline-flex items-center justify-center",
          "h-5 w-5 rounded-full",
          "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
          "transition-colors duration-[var(--duration-fast)]",
        )}
      >
        <X size={12} />
      </button>
    </motion.div>
  );
}

function ToastProvider() {
  const toasts = useToastStore((s) => s.toasts);
  const toastStyle = useUIStore((s) => s.toastStyle);
  const minimal = toastStyle === "minimal";

  return (
    <div
      aria-label="Notifications"
      className={cn(
        "fixed z-[60] flex flex-col pointer-events-none",
        minimal
          ? "top-3 right-3 gap-1.5 items-end"
          : "top-4 right-4 gap-2",
      )}
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((item) => (
          <ToastEntry key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </div>
  );
}

ToastProvider.displayName = "ToastProvider";

export { ToastProvider, useToast, useToastStore };
export type { ToastItem, ToastType, ToastState };
